/**
 * Der gemeinsame Editor einer Zahlungsvorlage: optionaler Kommentar plus Ratenzeilen (Prozent,
 * Anker, Tage nach Anker, je Rate eine Reihe), Zeilen hinzufuegen/entfernen/umsortieren, Summenanzeige. Er wird vom
 * Anlage-Dialog (`Suppliers/NeueVorlageDialog.tsx`) UND von der Seite "Zahlungsvorlagen"
 * (`ZahlungsvorlagenSeite.tsx`) benutzt - eine Implementierung, nicht zwei.
 *
 * KONTROLLIERT: der Aufrufer haelt den Entwurf und die Knoepfe (Speichern, Abbrechen, Loeschen), der
 * Editor zeichnet nur die Felder. `pruefeEntwurf` ist die EINE Pruefung, die beide Aufrufer vor dem
 * Senden fahren.
 *
 * Der Name der Vorlage wird nicht eingegeben (Nutzerentscheidung 01.10.2026): er lautet
 * "[Kommentar] - [Titel aus den Raten]" und wird hier live als Vorschau gezeigt
 * (`lib/zahlungsvorlagenTitel.ts`). Das Raten-Label (`rows[].label`) gibt es in der Oberflaeche nicht
 * mehr; bestehende Zeilen tragen es im Entwurf unveraendert weiter, neue Zeilen bekommen "".
 */
import type { JSX } from "react";
import { Ikone } from "../Ikone.tsx";
import { ANKER_LABEL, type Anker } from "../../lib/anker.ts";
import { vorlagenName } from "../../lib/zahlungsvorlagenTitel.ts";
import type { PaymentTemplatePayment } from "../../config/payments.ts";

export interface EntwurfZeile {
  /** Nicht mehr bearbeitbar; nur durchgereicht, damit nichts aus bestehenden Vorlagen verloren geht. */
  label: string;
  pct: string;
  anchor: Anker | null;
  offsetDays: string;
}

export interface VorlagenEntwurf {
  comment: string;
  zeilen: EntwurfZeile[];
}

const LEERE_ZEILE: EntwurfZeile = { label: "", pct: "", anchor: null, offsetDays: "0" };

export function leereZeile(): EntwurfZeile {
  return { ...LEERE_ZEILE };
}

export function leererEntwurf(): VorlagenEntwurf {
  return { comment: "", zeilen: [leereZeile()] };
}

export function entwurfAusVorlage(vorlage: {
  comment?: string | null;
  rows: readonly PaymentTemplatePayment[];
}): VorlagenEntwurf {
  const zeilen: EntwurfZeile[] = vorlage.rows.map((r) => ({
    label: r.label ?? "",
    pct: String(r.pct ?? ""),
    anchor: r.anchor ?? null,
    offsetDays: String(r.offsetDays ?? 0),
  }));
  return { comment: vorlage.comment ?? "", zeilen: zeilen.length > 0 ? zeilen : [leereZeile()] };
}

/** Die Zeilen des Entwurfs in der Form des Servers; ungueltige Zeilen ergeben `null`. */
function zeilenAlsRaten(zeilen: readonly EntwurfZeile[]): PaymentTemplatePayment[] | null {
  const raten: PaymentTemplatePayment[] = [];
  for (const z of zeilen) {
    const tage = z.offsetDays.trim() === "" ? NaN : Number(z.offsetDays);
    if (!Number.isInteger(tage)) return null;
    raten.push({ label: z.label, pct: String(prozentwert(z.pct)), anchor: z.anchor, offsetDays: tage });
  }
  return raten;
}

function prozentwert(roh: string): number {
  const text = roh.trim().replace(",", ".");
  return text === "" ? NaN : Number(text);
}

/** Die Summe der Prozentwerte, soweit lesbar (nicht lesbare zaehlen 0). Fuer die Anzeige. */
export function prozentsumme(zeilen: readonly EntwurfZeile[]): number {
  const summe = zeilen.reduce((acc, z) => {
    const wert = prozentwert(z.pct);
    return acc + (Number.isFinite(wert) ? wert : 0);
  }, 0);
  return Math.round(summe * 1000) / 1000;
}

export type Pruefergebnis =
  | { fehler: string }
  | { fehler: null; comment: string | null; rows: PaymentTemplatePayment[] };

/**
 * Die Pruefung VOR dem Senden: Tage nach Anker pro Zeile ganzzahlig (negativ erlaubt), Prozent pro
 * Zeile eine Zahl groesser 0, Summe genau 100 %. Der Server prueft Anker und Tage ebenfalls, die
 * Summe aber nicht - sie ist eine Eingabehilfe dieser Oberflaeche.
 */
export function pruefeEntwurf(entwurf: VorlagenEntwurf): Pruefergebnis {
  const { zeilen } = entwurf;
  if (zeilen.length === 0) return { fehler: "Mindestens eine Rate ist erforderlich." };
  for (const [index, zeile] of zeilen.entries()) {
    if (zeile.offsetDays.trim() === "") return { fehler: "Tage nach Anker: Angabe erforderlich." };
    if (!Number.isInteger(Number(zeile.offsetDays))) return { fehler: "Tage nach Anker: ganze Zahl erforderlich." };
    const pct = prozentwert(zeile.pct);
    if (!Number.isFinite(pct) || pct <= 0) {
      return { fehler: `Prozent der Zeile ${index + 1}: eine Zahl größer 0 ist erforderlich.` };
    }
  }
  const summe = prozentsumme(zeilen);
  if (Math.abs(summe - 100) > 0.001) {
    return { fehler: `Die Prozentwerte müssen zusammen 100 % ergeben (aktuell ${summe} %).` };
  }
  const rows = zeilenAlsRaten(zeilen);
  if (rows === null) return { fehler: "Tage nach Anker: ganze Zahl erforderlich." };
  const kommentar = entwurf.comment.trim();
  return { fehler: null, comment: kommentar === "" ? null : kommentar, rows };
}

/** Der Name, wie ihn der Server aus diesem Entwurf bilden wuerde (Vorschau). */
export function entwurfName(entwurf: VorlagenEntwurf): string {
  return vorlagenName(
    entwurf.comment,
    entwurf.zeilen.map((z) => ({ pct: z.pct, anchor: z.anchor, offsetDays: z.offsetDays })),
  );
}

export interface VorlagenEditorProps {
  entwurf: VorlagenEntwurf;
  onChange: (neu: VorlagenEntwurf) => void;
  /** Sperrt alle Eingaben (laufender Vorgang oder fehlendes Recht). */
  gesperrt?: boolean;
  /** Tooltip auf gesperrten Elementen (`NUR_LESEN_TITEL`). */
  sperrTitel?: string;
  /** Praefix fuer die `id`s - zwei Editoren auf einer Seite duerfen sich nicht ueberschneiden. */
  kennung?: string;
  /**
   * Der gespeicherte Name der Vorlage. Gesetzt (Seite "Zahlungsvorlagen"): die Namensvorschau erscheint
   * nur, solange der Entwurf davon abweicht - der Name steht sonst schon als Ueberschrift da.
   * Ungesetzt (Anlage-Dialog): die Vorschau ist immer sichtbar.
   */
  gespeicherterName?: string;
}

export function VorlagenEditor({
  entwurf,
  onChange,
  gesperrt = false,
  sperrTitel,
  kennung = "vorlage",
  gespeicherterName,
}: VorlagenEditorProps): JSX.Element {
  const { zeilen } = entwurf;
  const aendereZeile = (index: number, feld: keyof EntwurfZeile, wert: string | null): void =>
    onChange({ ...entwurf, zeilen: zeilen.map((z, i) => (i === index ? { ...z, [feld]: wert } : z)) });
  const verschiebe = (index: number, richtung: -1 | 1): void => {
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= zeilen.length) return;
    const neu = [...zeilen];
    [neu[index], neu[ziel]] = [neu[ziel], neu[index]];
    onChange({ ...entwurf, zeilen: neu });
  };
  const summe = prozentsumme(zeilen);
  const summeStimmt = Math.abs(summe - 100) <= 0.001;

  const vorschau = entwurfName(entwurf);
  const zeigeName = gespeicherterName === undefined || gespeicherterName !== vorschau;

  return (
    <div className="vorlagen-editor">
      <div className="field">
        <label htmlFor={`${kennung}-kommentar`}>Kommentar (optional)</label>
        <input
          id={`${kennung}-kommentar`}
          value={entwurf.comment}
          disabled={gesperrt}
          title={sperrTitel}
          onChange={(e) => onChange({ ...entwurf, comment: e.target.value })}
        />
      </div>
      <p className="vorlagen-name" aria-live="polite">
        {zeigeName ? (
          <>
            <span className="vorlagen-name-label">{gespeicherterName === undefined ? "Name: " : "Neuer Name: "}</span>
            <span className="vorlagen-name-wert">{vorschau}</span>
          </>
        ) : null}
      </p>
      <div className="vorlagen-raten">
        {zeilen.map((zeile, index) => (
          <div className="vorlagen-rate" key={index}>
            <span className="vorlagen-rate-prozent">
              <input
                aria-label={`Prozent der Zeile ${index + 1}`}
                inputMode="decimal"
                value={zeile.pct}
                disabled={gesperrt}
                title={sperrTitel}
                onChange={(e) => aendereZeile(index, "pct", e.target.value)}
              />
              <span aria-hidden="true">%</span>
            </span>
            <select
              className="vorlagen-rate-anker"
              aria-label={`Anker der Zeile ${index + 1}`}
              value={zeile.anchor ?? ""}
              disabled={gesperrt}
              title={sperrTitel}
              onChange={(e) => aendereZeile(index, "anchor", e.target.value === "" ? null : e.target.value)}
            >
              <option value="">Handdatum (kein Anker)</option>
              {Object.entries(ANKER_LABEL).map(([anker, text]) => (
                <option key={anker} value={anker}>
                  {text}
                </option>
              ))}
            </select>
            <span className="vorlagen-rate-tage">
              <span aria-hidden="true">+</span>
              <input
                type="number"
                step={1}
                aria-label={`Tage nach Anker, Zeile ${index + 1}`}
                value={zeile.offsetDays}
                disabled={gesperrt}
                title={sperrTitel}
                onWheel={(e) => e.currentTarget.blur()}
                onChange={(e) => aendereZeile(index, "offsetDays", e.target.value)}
              />
              <span aria-hidden="true">Tage</span>
            </span>
            <span className="vorlagen-zeilenaktionen">
              <button
                type="button"
                className="plan-zeile-weg"
                aria-label={`Zeile ${index + 1} nach oben`}
                disabled={gesperrt || index === 0}
                title={sperrTitel}
                onClick={() => verschiebe(index, -1)}
              >
                <Ikone name="arrow-up" />
              </button>
              <button
                type="button"
                className="plan-zeile-weg"
                aria-label={`Zeile ${index + 1} nach unten`}
                disabled={gesperrt || index === zeilen.length - 1}
                title={sperrTitel}
                onClick={() => verschiebe(index, 1)}
              >
                <Ikone name="arrow-down" />
              </button>
              <button
                type="button"
                className="plan-zeile-weg"
                aria-label={`Zeile ${index + 1} entfernen`}
                disabled={gesperrt || zeilen.length === 1}
                title={sperrTitel}
                onClick={() => onChange({ ...entwurf, zeilen: zeilen.filter((_, i) => i !== index) })}
              >
                <Ikone name="x" />
              </button>
            </span>
          </div>
        ))}
      </div>
      <div className="vorlagen-editor-fuss">
        <button
          type="button"
          className="plan-zeile-neu"
          disabled={gesperrt}
          title={sperrTitel}
          onClick={() => onChange({ ...entwurf, zeilen: [...zeilen, leereZeile()] })}
        >
          <Ikone name="circle-plus" /> Rate hinzufügen
        </button>
        <span className={summeStimmt ? "vorlagen-summe stimmt" : "vorlagen-summe abweichend"} role="status">
          Summe: {summe} %
        </span>
      </div>
    </div>
  );
}
